import { PLACEHOLDER_HOTKEY } from "lib/constants";
import { isArray, isString } from "lodash";
import React, { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { isMacOs } from "react-device-detect";
import { CommandIcon } from "@library/Icons";
import IconButton, { IconButtonProps } from "./IconButton";
import { useRouter } from "next/router";

interface WithShortcutProps {
  shortcut: string | string[] | null; // See https://react-hotkeys-hook.vercel.app/ for syntax of key combinations.
  noRepeat?: boolean;
}

// This is a wrapper around a standard Button which adds a the keyboard shortcut interaction.
// This isn't included within the Button component itself, as the various ways keypresses
// are added feels a bit hacky, and I'm wondering whether adding this extra hook
// to every button could be a performance hit (untested, probably untrue).
export const IconButtonWithShortcut = React.forwardRef(function ButtonWithShortcutComponent(
  props: IconButtonProps & WithShortcutProps,
  ref: React.ForwardedRef<HTMLButtonElement> | React.ForwardedRef<HTMLAnchorElement>,
) {
  const [buttonActive, setButtonActive] = React.useState(false);
  const enabled = (isString(props.shortcut) || isArray(props.shortcut)) && !props.disabled;
  const router = useRouter();

  // Copied from ButtonWithShortcut. Keys are not used.
  const shortcut = props.shortcut ?? PLACEHOLDER_HOTKEY;
  const keys: React.ReactNode[] =
    (isArray(props.shortcut) ? props.shortcut[0] : props.shortcut)?.split("+").map((key) => {
      if (["cmd", "command", "mod"].includes(key)) {
        if (isMacOs) return <CommandIcon key={key} className="h-12 w-12" />;
        return "Ctrl";
      }
      return key;
    }) || [];

  const isKeyCombo = keys.length > 1;

  useHotkeys(
    shortcut,
    (event) => {
      if (event.type === "keydown") {
        setButtonActive(true);
        if (isKeyCombo) {
          setTimeout(() => {
            setButtonActive(false);
          }, 100);
        }
      } else if (event.type === "keyup") {
        setButtonActive(false);
        return;
      }
      if (props.noRepeat && event.repeat) {
        return;
      }
      // TODO: bit of hack using a mouse handler as a keyboard handler, potentially we should generalise to onSelect
      props.onClick && props.onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
      props.href && router.push(props.href);
    },
    { enabled: enabled, keydown: true, keyup: true },
    [shortcut, enabled, props.noRepeat, props.onClick],
  );

  // There was a bug where if the button became disabled, the active state would persist.
  useEffect(() => {
    if (props.disabled) {
      setButtonActive(false);
    }
  }, [props.disabled]);

  return <IconButton ref={ref as any} {...props} active={buttonActive || props.active} />;
});
